import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { BehaviorSubject, Observable, finalize, take, tap } from 'rxjs'
import { MatTableDataSource } from '@angular/material/table'
import { TokenizedMessageLoadingService } from '../tokenized-message-loading/tokenized-message-loading.service'
import { ErrorService } from '../error.service'
import { KeyingAppEnvironmentConfig } from '../../configuration/keying.app.environment.config'
import { Note } from '../../models/dev.test.models'
import { NoteType } from '../../constants/noteType.enum'

export interface CreateNoteRequest {
  message: string
  noteType: number
  dateSent?: Date
  email?: string
  orderNumber?: string
}

/**
 * Service for managing notes within key structures
 *
 * Provides comprehensive note management functionality including CRUD operations,
 * reactive data streams, filtering capabilities, and Material table integration.
 * Primarily used by Dashboard component for structure-based note management.
 *
 * Key features:
 * - Structure-scoped note operations (create, read, update, soft delete)
 * - Reactive data management with BehaviorSubjects and Observables
 * - Text and note type filtering for Material tables
 * - Tokenized loading states for concurrent operations
 * - Three note types: Structure, N-Order, and Bitting List
 */
@Injectable({
  providedIn: 'root',
})
export class NotesService {
  private notesSubject = new BehaviorSubject<Note[]>([])
  private dataSourceSubject = new BehaviorSubject<MatTableDataSource<Note>>(new MatTableDataSource([]))
  private currentStructureId: number | null = null
  private currentFilter = ''

  notes$ = this.notesSubject.asObservable()
  dataSource$ = this.dataSourceSubject.asObservable()

  constructor(
    private httpClient: HttpClient,
    private tokenizedMessageLoadingService: TokenizedMessageLoadingService,
    private errorService: ErrorService,
    private keyingAppEnvironmentConfig: KeyingAppEnvironmentConfig
  ) {}

  loadNotesForStructure(structureId: number, forceReload: boolean = false): void {
    if (!structureId) {
      return
    }

    if (!forceReload && this.currentStructureId === structureId) {
      return
    }
    this.currentStructureId = structureId

    const token = this.tokenizedMessageLoadingService.startLoading('Loading Notes', 'load-notes', 'medium')

    this.getNotesFromApi(structureId)
      .pipe(
        take(1),
        finalize(() => this.tokenizedMessageLoadingService.stopLoading(token))
      )
      .subscribe({
        next: (notes) => {
          const processedNotes = this.processNotes(notes)
          this.notesSubject.next(processedNotes)
          this.updateDataSource(processedNotes)
        },
        error: (error) => {
          this.errorService.throw(error)
          this.notesSubject.next([])
          this.updateDataSource([])
        },
      })
  }

  createNote(noteRequest: CreateNoteRequest, structureId: number): Observable<any> {
    const token = this.tokenizedMessageLoadingService.startLoading('Creating Note', 'create-note', 'medium')

    return this.createNoteApi(noteRequest, structureId).pipe(
      take(1),
      tap(() => {
        this.reloadNotes(structureId)
      }),
      finalize(() => this.tokenizedMessageLoadingService.stopLoading(token))
    )
  }

  updateNote(noteId: number, message: string, structureId: number): Observable<any> {
    const request = [
      {
        operationType: 0,
        op: 'replace',
        path: '/message',
        value: message,
      },
    ]

    const token = this.tokenizedMessageLoadingService.startLoading('Updating Note', 'update-note', 'medium')

    return this.updateNoteApi(request, structureId, noteId).pipe(
      take(1),
      tap(() => {
        this.refreshTableData()
      }),
      finalize(() => this.tokenizedMessageLoadingService.stopLoading(token))
    )
  }

  deleteNote(noteId: number, structureId: number): Observable<any> {
    const request = [
      {
        operationType: 0,
        op: 'replace',
        path: '/isDeleted',
        value: true,
      },
    ]

    const token = this.tokenizedMessageLoadingService.startLoading('Deleting Note', 'delete-note', 'medium')

    return this.updateNoteApi(request, structureId, noteId).pipe(
      take(1),
      tap(() => {
        this.reloadNotes(structureId)
      }),
      finalize(() => this.tokenizedMessageLoadingService.stopLoading(token))
    )
  }

  applyFilter(filterValue: string): void {
    const currentDataSource = this.dataSourceSubject.value
    if (currentDataSource) {
      currentDataSource.filter = filterValue.trim().toLowerCase()
    }
  }

  filterTableByType(type: string): void {
    if (type === '' || type.toLowerCase() === 'all') {
      this.currentFilter = ''
    } else {
      this.currentFilter = type.trim().toLowerCase()
    }

    const currentDataSource = this.dataSourceSubject.value
    if (currentDataSource) {
      currentDataSource.filter = this.currentFilter
    }
  }

  getCurrentNotes(): Note[] {
    return this.notesSubject.value
  }

  getCurrentDataSource(): MatTableDataSource<Note> {
    return this.dataSourceSubject.value
  }

  clearNotes(): void {
    this.notesSubject.next([])
    this.updateDataSource([])
    this.currentStructureId = null
  }

  private reloadNotes(structureId: number): void {
    this.currentStructureId = null
    this.loadNotesForStructure(structureId, true)
  }

  private processNotes(notes: Note[]): Note[] {
    if (!notes || !Array.isArray(notes)) {
      return []
    }

    const activeNotes = notes.filter((note) => note.isDeleted === false)

    activeNotes.sort((a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime())

    activeNotes.forEach((note) => {
      if (note.noteType === NoteType.Structure) {
        note.noteTypeName = 'Structure'
      } else if (note.noteType === NoteType.NOrder) {
        note.noteTypeName = 'N-Order'
      } else if (note.noteType === NoteType.BittingList) {
        note.noteTypeName = 'Bitting List'
      } else if (note.noteType === NoteType.Fsic) {
        note.noteTypeName = 'FSIC'
      }
    })

    return activeNotes
  }

  private updateDataSource(notes: Note[]): void {
    const dataSource = new MatTableDataSource(notes)

    dataSource.filterPredicate = (note: Note, filter: string) => {
      if (!filter) {
        return true
      }

      const noteTypeName = note.noteTypeName?.toLowerCase() || ''
      return noteTypeName.includes(filter.toLowerCase())
    }

    dataSource.filter = this.currentFilter

    this.dataSourceSubject.next(dataSource)
  }

  private refreshTableData(): void {
    const currentNotes = this.notesSubject.value
    this.updateDataSource(currentNotes)
  }

  // HTTP API Methods
  private getNotesFromApi(structureId: number): Observable<Note[]> {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/Notes`
    return this.httpClient.get<Note[]>(url)
  }

  private createNoteApi(note: CreateNoteRequest, structureId: number): Observable<any> {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/Notes`
    return this.httpClient.post<any>(url, note)
  }

  private updateNoteApi(request: any[], structureId: number, noteId: number): Observable<any> {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/Notes/${noteId}`
    return this.httpClient.patch<any>(url, request)
  }
}
